// DataTrace Transaction Management
// Centralized logic for managing DataTrace API transactions, history, and status checking

import { getCredentials } from './datatraceApi';

// Save transaction to persistent storage
export const saveTransaction = async (transaction) => {
  try {
    const response = await fetch('/api/datatrace/save-transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction })
    });

    if (!response.ok) {
      throw new Error(`Failed to save transaction: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to save transaction:', error);
    throw error;
  }
};

// Load transaction history from server
export const loadTransactionHistory = async () => {
  try {
    const response = await fetch('/api/datatrace/transaction-history');
    
    if (!response.ok) {
      throw new Error(`Failed to load transaction history: ${response.status}`);
    }

    const data = await response.json();
    return data.transactions || [];
  } catch (error) {
    console.error('Failed to load transaction history:', error);
    return [];
  }
};

// Update transaction status
export const updateTransactionStatus = async (transactionId, statusData) => {
  try {
    const response = await fetch('/api/datatrace/update-transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, statusData })
    });

    if (!response.ok) {
      throw new Error(`Failed to update transaction: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to update transaction:', error);
    throw error;
  }
};

// Check transaction status via DataTrace API
export const checkTransactionStatus = async (transactionId) => {
  const credentials = getCredentials();

  if (!credentials.clientId || !credentials.clientSecret) {
    throw new Error('DataTrace credentials not configured');
  }

  try {
    const response = await fetch('/api/datatrace/transaction-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credentials: {
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          scope: credentials.scope
        },
        transactionId
      })
    });

    const responseText = await response.text();
    let data;

    try {
      data = JSON.parse(responseText);
    } catch (e) {
      data = {
        error: 'Failed to parse response',
        rawResponse: responseText
      };
    }

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    return {
      status: data.status,
      transactionDetails: data.transactionDetails,
      fullResponse: data,
      rawResponse: responseText,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to check transaction status:', error);
    throw error;
  }
};

// Get transaction report (retrieve completed report)
export const getTransactionReport = async (transactionId, productCode) => {
  const credentials = getCredentials();

  if (!credentials.clientId || !credentials.clientSecret) {
    throw new Error('DataTrace credentials not configured');
  }

  try {
    const response = await fetch('/api/datatrace/get-report', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credentials: {
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          scope: credentials.scope
        },
        transactionId,
        productCode
      })
    });

    const responseText = await response.text();
    let data;

    try {
      data = JSON.parse(responseText);
    } catch (e) {
      data = {
        error: 'Failed to parse response',
        rawResponse: responseText
      };
    }

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    return {
      report: data,
      rawResponse: responseText,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to retrieve report:', error);
    throw error;
  }
};

// Check for duplicate searches
export const findDuplicateSearch = (transactionHistory, searchParams, productCode, productType) => {
  const searchKey = `${searchParams.address.trim()}, ${searchParams.city.trim()}, ${searchParams.state} ${searchParams.zip.trim()}`.toLowerCase();
  
  return transactionHistory.find(t =>
    t.searchKey === searchKey &&
    t.productCode === productCode &&
    t.productType === productType &&
    (!t.status || t.status === 'pending' || t.status === 'processing')
  );
};

// Generate search key for deduplication
export const generateSearchKey = (searchParams) => {
  return `${searchParams.address}, ${searchParams.city}, ${searchParams.state} ${searchParams.zip}`.toLowerCase();
};

// Create transaction object for saving
export const createTransactionRecord = (searchParams, productConfig, apiResponse, requestBody) => {
  const searchKey = generateSearchKey(searchParams);

  // Extract status from API response
  // For sync property_data: Success is implicit (data exists, no errors)
  // For async products (tax, tiqsa): status field indicates "pending", "processing", etc.
  let responseStatus;
  
  // Check if this is a synchronous product (property_data)
  const isSyncProduct = productConfig.productCode === 'property_data';
  
  if (isSyncProduct) {
    // For sync products: if we have data and no errors, it's FULFILLED
    const hasErrors = apiResponse.data?.errors && apiResponse.data.errors.length > 0;
    const hasData = apiResponse.data?.data || apiResponse.data?.transaction_id;
    
    if (hasErrors) {
      responseStatus = 'FAILED';
    } else if (hasData) {
      responseStatus = 'FULFILLED';
    } else {
      responseStatus = 'UNKNOWN';
    }
  } else {
    // For async products: check status fields
    responseStatus = apiResponse.data?.report_status || apiResponse.data?.status || 'pending';
  }

  // Build base transaction record
  const transaction = {
    transactionId: apiResponse.data?.transaction_id,
    address: `${searchParams.address}, ${searchParams.city}, ${searchParams.state} ${searchParams.zip}`,
    searchKey,
    productCode: productConfig.productCode,
    productType: productConfig.productType,
    endpoint: productConfig.endpoint,
    timestamp: new Date().toISOString(),
    status: responseStatus,
    searchParams,
    confirmationNumber: apiResponse.data?.confirmation_number || apiResponse.data?.servicing_platform_confirmation_number,
    requestPayload: requestBody,
    clientReference: requestBody?.client_reference_number,
    clientOrderNumber: requestBody?.client_order_number
  };

  // For synchronous responses or completed async responses, save the full response
  if (responseStatus && responseStatus.trim().toUpperCase() !== 'PENDING') {
    transaction.responseData = apiResponse.data;
    transaction.statusResponse = {
      status: responseStatus,
      transactionDetails: {
        product: productConfig.productCode,
        asOfDate: new Date().toLocaleString('en-US'),
        confirmationNumber: transaction.confirmationNumber,
        clientReference: transaction.clientReference
      }
    };
  }

  return transaction;
};


export default {
  saveTransaction,
  loadTransactionHistory,
  updateTransactionStatus,
  checkTransactionStatus,
  getTransactionReport,
  findDuplicateSearch,
  generateSearchKey,
  createTransactionRecord
};
